import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  childrensBookIllustrationsByUnit,
  currentChildrensBookCover,
} from "../utils/childrensBookAssetState";
import {
  reviewManuscriptUnitCapacity,
} from "../utils/childrensBookManuscriptCapacity";
import {
  plainTextFromManuscriptEntry,
} from "../utils/childrensBookManuscriptContent";

import ChildrensBookManuscriptReadOnly from "./ChildrensBookManuscriptReadOnly";

const EMPTY_ASSETS = [];

const PUBLISHED_READER_VISIBILITIES =
  new Set([
    "private_draft",
    "nextscenes_readers",
    "published",
  ]);

function normalizePublishedReaderVisibility(
  value
) {
  const visibility =
    clean(value).toLowerCase();

  return PUBLISHED_READER_VISIBILITIES
    .has(visibility)
      ? visibility
      : "private_draft";
}

function clean(value) {
  return String(value ?? "").trim();
}

function safeImageUrl(value) {
  const candidate = clean(value);

  if (!candidate) return "";

  try {
    const parsed = new URL(candidate);

    return (
      parsed.protocol === "http:" ||
      parsed.protocol === "https:"
    )
      ? parsed.toString()
      : "";
  } catch {
    return "";
  }
}

function protectedAsset(asset) {
  return Boolean(
    asset &&
    (
      asset.source === "upload" ||
      asset.storageProvider === "local"
    )
  );
}

function publicationDate(value, isFR) {
  const raw = clean(value);

  if (!raw) {
    return isFR
      ? "Date non disponible"
      : "Date not available";
  }

  const date = new Date(raw);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return raw;
  }

  try {
    return new Intl.DateTimeFormat(
      isFR ? "fr" : "en",
      {
        dateStyle: "medium",
        timeStyle: "short",
      }
    ).format(date);
  } catch {
    return raw;
  }
}

function unitLabel(unit, index, total, isFR) {
  const type =
    unit?.unitType ===
      "double_page_spread"
      ? (
          isFR
            ? "Double page"
            : "Two-page spread"
        )
      : (
          isFR
            ? "Page"
            : "Page"
        );

  return `${type} ${index + 1} / ${total}`;
}

export default function ChildrensBookPublishedBook({
  lang = "en",
  projectPlan = {},
  projectVisibility = "private_draft",
  childrensBookAssets = EMPTY_ASSETS,
  childrensBookAssetsLoading = false,
  childrensBookAssetsError = "",
  onLoadChildrensBookAssetFile,
  onUpdateVisibility,
  onOpenPublicationHandoff,
  onReturn,
  onExit,
}) {
  const isFR =
    String(lang).toLowerCase() ===
    "fr";

  const publicationRecord =
    projectPlan.publicationReadiness || {};

  const published =
    publicationRecord.status ===
      "published_nextscenes";

  const normalizedReaderVisibility =
    normalizePublishedReaderVisibility(
      projectVisibility
    );

  const [
    readerVisibility,
    setReaderVisibility,
  ] = useState(
    normalizedReaderVisibility
  );

  const [
    savedReaderVisibility,
    setSavedReaderVisibility,
  ] = useState(
    normalizedReaderVisibility
  );

  const [
    visibilitySaving,
    setVisibilitySaving,
  ] = useState(false);

  const [
    visibilityMessage,
    setVisibilityMessage,
  ] = useState("");

  const [
    visibilityError,
    setVisibilityError,
  ] = useState("");

  useEffect(() => {
    setReaderVisibility(
      normalizedReaderVisibility
    );

    setSavedReaderVisibility(
      normalizedReaderVisibility
    );

    setVisibilityMessage("");
    setVisibilityError("");
  }, [
    normalizedReaderVisibility,
  ]);

  async function saveReaderVisibility() {
    if (
      typeof onUpdateVisibility !==
      "function"
    ) {
      return;
    }

    try {
      setVisibilitySaving(true);
      setVisibilityMessage("");
      setVisibilityError("");

      const result =
        await onUpdateVisibility(
          readerVisibility
        );

      if (result?.ok === false) {
        throw new Error(
          clean(result?.error) ||
          "Could not save reader access."
        );
      }

      const savedVisibility =
        normalizePublishedReaderVisibility(
          result?.work?.visibility ||
          readerVisibility
        );

      setReaderVisibility(
        savedVisibility
      );

      setSavedReaderVisibility(
        savedVisibility
      );

      setVisibilityMessage(
        isFR
          ? "Accès des lecteurs enregistré."
          : "Reader access saved."
      );
    } catch (error) {
      setVisibilityError(
        clean(error?.message) ||
        (
          isFR
            ? "Impossible d’enregistrer l’accès des lecteurs."
            : "Could not save reader access."
        )
      );
    } finally {
      setVisibilitySaving(false);
    }
  }

  const title =
    clean(projectPlan.identity?.title) ||
    clean(projectPlan.coverPlan?.title) ||
    (
      isFR
        ? "Livre pour enfants sans titre"
        : "Untitled Children’s Book"
    );

  const author =
    clean(projectPlan.identity?.penName) ||
    clean(projectPlan.identity?.authorName) ||
    clean(projectPlan.coverPlan?.authorName) ||
    (
      isFR
        ? "Auteur non renseigné"
        : "Author not provided"
    );

  const units =
    useMemo(
      () =>
        Array.isArray(
          projectPlan.bookMap?.units
        )
          ? projectPlan.bookMap.units
          : [],
      [projectPlan.bookMap?.units]
    );

  const manuscripts =
    useMemo(
      () =>
        Array.isArray(
          projectPlan
            .manuscriptPlan
            ?.entries
        )
          ? projectPlan
              .manuscriptPlan
              .entries
          : [],
      [
        projectPlan
          .manuscriptPlan
          ?.entries,
      ]
    );

  const manuscriptsByUnit =
    useMemo(
      () =>
        manuscripts.reduce(
          (result, entry) => {
            const id =
              clean(entry?.unitId);

            if (id) {
              result[id] = entry;
            }

            return result;
          },
          {}
        ),
      [manuscripts]
    );

  const activeCover =
    useMemo(
      () =>
        currentChildrensBookCover(
          childrensBookAssets,
          "front_cover"
        ),
      [childrensBookAssets]
    );

  const illustrationsByUnit =
    useMemo(
      () =>
        childrensBookIllustrationsByUnit(
          childrensBookAssets
        ),
      [childrensBookAssets]
    );

  const [
    activeIndex,
    setActiveIndex,
  ] = useState(0);

  const activeUnitId =
    activeIndex > 0
      ? clean(
          units[
            activeIndex - 1
          ]?.id
        )
      : "";

  const activeDisplayAsset =
    activeIndex === 0
      ? activeCover
      : (
          illustrationsByUnit[
            activeUnitId
          ] || null
        );

  const displayAssets =
    useMemo(
      () =>
        published &&
        activeDisplayAsset
          ? [activeDisplayAsset]
          : [],
      [
        activeDisplayAsset,
        published,
      ]
    );

  const [
    protectedUrls,
    setProtectedUrls,
  ] = useState({});

  const [
    protectedErrors,
    setProtectedErrors,
  ] = useState({});

  const [
    loadingProtectedAssets,
    setLoadingProtectedAssets,
  ] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const objectUrls = [];

    const assetsToLoad =
      displayAssets.filter(
        protectedAsset
      );

    setProtectedUrls({});
    setProtectedErrors({});

    if (
      assetsToLoad.length === 0 ||
      typeof onLoadChildrensBookAssetFile !==
        "function"
    ) {
      setLoadingProtectedAssets(
        false
      );

      return () => {};
    }

    setLoadingProtectedAssets(true);

    async function loadAssets() {
      const nextUrls = {};
      const nextErrors = {};

      for (
        const asset of assetsToLoad
      ) {
        const id =
          clean(asset?.id);

        const sourceUrl =
          clean(asset?.publicUrl);

        if (
          !id ||
          !sourceUrl
        ) {
          continue;
        }

        try {
          const blob =
            await onLoadChildrensBookAssetFile(
              sourceUrl
            );

          if (cancelled) return;

          if (
            typeof URL ===
              "undefined" ||
            typeof URL.createObjectURL !==
              "function"
          ) {
            throw new Error(
              "Object URL unavailable"
            );
          }

          const objectUrl =
            URL.createObjectURL(blob);

          objectUrls.push(
            objectUrl
          );

          nextUrls[id] =
            objectUrl;
        } catch {
          nextErrors[id] =
            isFR
              ? "Impossible de charger cette image publiée."
              : "Could not load this published image.";
        }
      }

      if (!cancelled) {
        setProtectedUrls(
          nextUrls
        );

        setProtectedErrors(
          nextErrors
        );
      }
    }

    loadAssets().finally(() => {
      if (!cancelled) {
        setLoadingProtectedAssets(
          false
        );
      }
    });

    return () => {
      cancelled = true;

      if (
        typeof URL !==
          "undefined" &&
        typeof URL.revokeObjectURL ===
          "function"
      ) {
        objectUrls.forEach(
          (url) =>
            URL.revokeObjectURL(
              url
            )
        );
      }
    };
  }, [
    displayAssets,
    isFR,
    onLoadChildrensBookAssetFile,
  ]);

  function imageUrl(asset) {
    if (!asset) return "";

    if (
      protectedAsset(asset)
    ) {
      return (
        protectedUrls[
          clean(asset.id)
        ] || ""
      );
    }

    return safeImageUrl(
      asset.publicUrl
    );
  }

  const items =
    useMemo(
      () => [
        {
          id: "published-cover",
          kind: "cover",
          asset: activeCover,
          label: isFR
            ? "Couverture"
            : "Cover",
        },

        ...units.map(
          (unit, index) => ({
            id:
              clean(unit?.id) ||
              `published-unit-${
                index + 1
              }`,

            kind: "unit",
            unit,

            manuscript:
              manuscriptsByUnit[
                unit.id
              ] || null,

            illustration:
              illustrationsByUnit[
                unit.id
              ] || null,

            label:
              unitLabel(
                unit,
                index,
                units.length,
                isFR
              ),
          })
        ),
      ],
      [
        activeCover,
        illustrationsByUnit,
        isFR,
        manuscriptsByUnit,
        units,
      ]
    );



  useEffect(() => {
    setActiveIndex(
      (current) =>
        items.length === 0
          ? 0
          : Math.min(
              current,
              items.length - 1
            )
    );
  }, [items.length]);

  const activeItem =
    items[activeIndex] || null;

  if (!published) {
    return (
      <section
        aria-label={
          isFR
            ? "Livre publié"
            : "Published Book"
        }
        className="ns-nbb-workspace-page"
      >
        <div
          className="ns-card ns-nbb-major-panel"
          style={{
            padding: 22,
            borderRadius: 18,
            background: "#f8fafc",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              padding: "5px 9px",
              borderRadius: 999,
              background: "#e2e8f0",
              color: "#475569",
              fontSize: 12,
              fontWeight: 900,
            }}
          >
            {isFR
              ? "Non publié"
              : "Not published"}
          </span>

          <h2
            className="ns-section-title"
            style={{
              margin: "12px 0 8px",
            }}
          >
            {isFR
              ? "Livre publié"
              : "Published Book"}
          </h2>

          <p
            className="ns-card-meta"
            style={{
              margin: 0,
              maxWidth: 760,
              lineHeight: 1.65,
            }}
          >
            {isFR
              ? "Terminez la transmission pour publication et confirmez « Publier sur NextScenes » avant d’ouvrir l’expérience finale du livre."
              : "Complete Publication Handoff and confirm “Publish on NextScenes” before opening the final published-book experience."}
          </p>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              marginTop: 18,
            }}
          >
            <button
              className="ns-button ns-button-primary"
              type="button"
              onClick={
                onOpenPublicationHandoff
              }
            >
              {isFR
                ? "Ouvrir la transmission pour publication"
                : "Open Publication Handoff"}
            </button>

            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={onReturn}
            >
              {isFR
                ? "Retour au Blueprint"
                : "Return to Blueprint"}
            </button>
          </div>
        </div>
      </section>
    );
  }

  const canGoPrevious =
    activeIndex > 0;

  const canGoNext =
    activeIndex <
      items.length - 1;

  const publishedDate =
    publicationDate(
      publicationRecord.publishedAt,
      isFR
    );

  const activeImage =
    activeItem?.kind ===
      "cover"
      ? imageUrl(
          activeItem.asset
        )
      : imageUrl(
          activeItem?.illustration
        );

  const imageError =
    activeItem?.kind ===
      "cover"
      ? protectedErrors[
          clean(
            activeItem.asset?.id
          )
        ]
      : protectedErrors[
          clean(
            activeItem
              ?.illustration
              ?.id
          )
        ];

  const manuscriptText =
    clean(
      plainTextFromManuscriptEntry(
        activeItem
          ?.manuscript
      )
    );

  const activeCapacity =
    activeItem?.kind === "unit"
      ? reviewManuscriptUnitCapacity(
          activeItem.unit,
          activeItem.manuscript
        )
      : null;

  const activeUnitOversized =
    Boolean(
      activeCapacity?.oversized
    );

  return (
    <section
      aria-label={
        isFR
          ? "Livre publié"
          : "Published Book"
      }
      className="ns-nbb-workspace-page"
    >
      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: "flex-start",
          gap: 16,
          flexWrap: "wrap",
          marginBottom: 18,
        }}
      >
        <div>
          <span
            style={{
              display: "inline-flex",
              padding: "6px 10px",
              borderRadius: 999,
              background: "#dcfce7",
              color: "#166534",
              fontSize: 12,
              fontWeight: 900,
              letterSpacing:
                "0.06em",
              textTransform:
                "uppercase",
            }}
          >
            {isFR
              ? "Publié sur NextScenes"
              : "Published on NextScenes"}
          </span>

          <h2
            className="ns-section-title"
            style={{
              margin: "10px 0 5px",
            }}
          >
            {isFR
              ? `Livre publié — ${title}`
              : `Published Book — ${title}`}
          </h2>

          <p
            className="ns-card-meta"
            style={{
              margin: 0,
              lineHeight: 1.55,
            }}
          >
            {isFR
              ? `Par ${author} · Publié le ${publishedDate}`
              : `By ${author} · Published ${publishedDate}`}
          </p>
        </div>

        {typeof onExit ===
        "function" ? (
          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={onExit}
          >
            {isFR
              ? "Retour à Mes projets"
              : "Return to My Projects"}
          </button>
        ) : null}
      </div>

      {typeof onUpdateVisibility ===
      "function" ? (
        <section
          className="ns-card"
          aria-label={
            isFR
              ? "Accès des lecteurs"
              : "Reader access"
          }
          style={{
            padding: 18,
            marginBottom: 16,
            borderRadius: 16,
          }}
        >
          <h3
            className="ns-card-title"
            style={{
              margin: "0 0 6px",
            }}
          >
            {isFR
              ? "Qui peut prévisualiser ce livre ?"
              : "Who can preview this book?"}
          </h3>

          <p
            className="ns-card-meta"
            style={{
              margin: "0 0 14px",
              lineHeight: 1.55,
            }}
          >
            {isFR
              ? "La publication et l’accès à l’aperçu sont deux décisions distinctes."
              : "Publication and preview access are separate decisions. Choose who can open this published book preview."}
          </p>

          <label
            style={{
              display: "grid",
              gap: 7,
              maxWidth: 440,
            }}
          >
            <strong>
              {isFR
                ? "Accès des lecteurs"
                : "Reader access"}
            </strong>

            <select
              aria-label={
                isFR
                  ? "Qui peut prévisualiser ce livre ?"
                  : "Who can preview this book?"
              }
              value={
                readerVisibility
              }
              disabled={
                visibilitySaving
              }
              onChange={(event) => {
                setReaderVisibility(
                  normalizePublishedReaderVisibility(
                    event.target.value
                  )
                );

                setVisibilityMessage("");
                setVisibilityError("");
              }}
              style={{
                minHeight: 44,
                padding: "9px 11px",
                borderRadius: 10,
                border:
                  "1px solid #cbd5e1",
                background: "#ffffff",
              }}
            >
              <option value="private_draft">
                {isFR
                  ? "Privé"
                  : "Private"}
              </option>

              <option value="nextscenes_readers">
                {isFR
                  ? "Lecteurs NextScenes"
                  : "NextScenes Readers"}
              </option>

              <option value="published">
                Public
              </option>
            </select>
          </label>

          <p
            className="ns-card-meta"
            style={{
              margin: "10px 0 0",
              lineHeight: 1.5,
            }}
          >
            {readerVisibility ===
            "published"
              ? (
                  isFR
                    ? "Tout le monde peut prévisualiser le livre et il peut apparaître dans Livres pour enfants."
                    : "Anyone can preview the book and it can appear in Children’s Books."
                )
              : readerVisibility ===
                "nextscenes_readers"
              ? (
                  isFR
                    ? "Seuls les lecteurs NextScenes connectés peuvent prévisualiser le livre."
                    : "Signed-in NextScenes readers can preview the book."
                )
              : (
                  isFR
                    ? "Le livre reste privé pour les lecteurs."
                    : "The book remains private from readers."
                )}
          </p>

          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              flexWrap: "wrap",
              marginTop: 14,
            }}
          >
            <button
              className="ns-button ns-button-primary"
              type="button"
              disabled={
                visibilitySaving ||
                readerVisibility ===
                  savedReaderVisibility
              }
              onClick={
                saveReaderVisibility
              }
            >
              {visibilitySaving
                ? (
                    isFR
                      ? "Enregistrement…"
                      : "Saving…"
                  )
                : (
                    isFR
                      ? "Enregistrer l’accès"
                      : "Save Reader Access"
                  )}
            </button>

            {visibilityMessage ? (
              <span
                role="status"
                style={{
                  color: "#166534",
                  fontWeight: 800,
                }}
              >
                {visibilityMessage}
              </span>
            ) : null}
          </div>

          {visibilityError ? (
            <div
              role="alert"
              style={{
                marginTop: 12,
                color: "#991b1b",
                fontWeight: 750,
              }}
            >
              {visibilityError}
            </div>
          ) : null}
        </section>
      ) : null}

      {childrensBookAssetsError ? (
        <div
          role="alert"
          className="ns-card"
          style={{
            padding: 14,
            marginBottom: 14,
            borderRadius: 14,
            color: "#991b1b",
          }}
        >
          {childrensBookAssetsError}
        </div>
      ) : null}

      {childrensBookAssetsLoading ||
      loadingProtectedAssets ? (
        <div
          role="status"
          className="ns-card-meta"
          style={{
            marginBottom: 14,
            fontWeight: 800,
          }}
        >
          {isFR
            ? "Chargement du livre publié…"
            : "Loading published book…"}
        </div>
      ) : null}

      <div
        className="ns-card"
        style={{
          padding: 14,
          marginBottom: 16,
          borderRadius: 16,
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={!canGoPrevious}
          onClick={() =>
            setActiveIndex(
              (current) =>
                Math.max(
                  0,
                  current - 1
                )
            )
          }
        >
          {isFR
            ? "Précédent"
            : "Previous"}
        </button>

        <strong
          aria-live="polite"
          style={{
            color: "#334155",
          }}
        >
          {activeItem?.label ||
            (
              isFR
                ? "Aucun contenu"
                : "No content"
            )}
        </strong>

        <button
          className="ns-button ns-button-primary"
          type="button"
          disabled={!canGoNext}
          onClick={() =>
            setActiveIndex(
              (current) =>
                Math.min(
                  items.length - 1,
                  current + 1
                )
            )
          }
        >
          {isFR
            ? "Suivant"
            : "Next"}
        </button>
      </div>

      {activeUnitOversized ? (
        <div
          role="alert"
          className="ns-card"
          style={{
            padding: 16,
            marginBottom: 16,
            borderRadius: 16,
            background: "#fff7ed",
            border:
              "1px solid rgba(234,88,12,0.32)",
            color: "#9a3412",
            lineHeight: 1.6,
          }}
        >
          <strong>
            {isFR
              ? "Cette unité doit être divisée."
              : "This unit needs to be divided."}
          </strong>

          <p
            style={{
              margin: "7px 0 0",
            }}
          >
            {isFR
              ? `Cette page ou double page contient ${activeCapacity.wordCount} mots. Divisez-la en pages ou doubles pages supplémentaires avant de la considérer comme une mise en page publiée finale.`
              : `This page or spread contains ${activeCapacity.wordCount} words. Divide it into additional pages or spreads before treating it as a final published layout.`}
          </p>
        </div>
      ) : null}

      <article
        className="ns-card ns-nbb-major-panel"
        style={{
          padding:
            "clamp(20px, 4vw, 38px)",
          borderRadius: 20,
          minHeight: 420,
          background:
            "linear-gradient(145deg, #ffffff, #fffaf5)",
        }}
      >
        {activeItem?.kind ===
        "cover" ? (
          <div
            style={{
              minHeight: 380,
              display: "grid",
              placeItems: "center",
              textAlign: "center",
            }}
          >
            <div>
              <h3
                className="ns-card-title"
                style={{
                  margin: "0 0 5px",
                  fontSize:
                    "clamp(26px, 4vw, 42px)",
                }}
              >
                {title}
              </h3>

              <p
                className="ns-card-meta"
                style={{
                  margin: "0 0 20px",
                  fontSize: 16,
                }}
              >
                {author}
              </p>

              {activeImage ? (
                <img
                  src={activeImage}
                  alt={
                    isFR
                      ? `Couverture publiée de ${title}`
                      : `${title} published cover`
                  }
                  style={{
                    display: "block",
                    width: "auto",
                    maxWidth:
                      "min(430px, 100%)",
                    maxHeight: 600,
                    margin: "0 auto",
                    objectFit: "contain",
                    borderRadius: 14,
                  }}
                />
              ) : (
                <p
                  className="ns-card-meta"
                  style={{
                    margin: 0,
                  }}
                >
                  {imageError ||
                    (
                      isFR
                        ? "La couverture publiée n’est pas disponible."
                        : "The published cover is unavailable."
                    )}
                </p>
              )}
            </div>
          </div>
        ) : activeItem ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                activeImage
                  ? "minmax(0, 1fr) minmax(0, 1fr)"
                  : "minmax(0, 1fr)",
              gap: 28,
              alignItems: "center",
            }}
          >
            {activeImage ? (
              <img
                src={activeImage}
                alt={
                  isFR
                    ? `Illustration publiée — ${activeItem.label}`
                    : `Published illustration — ${activeItem.label}`
                }
                style={{
                  display: "block",
                  width: "100%",
                  maxHeight: 620,
                  objectFit: "contain",
                  borderRadius: 14,
                }}
              />
            ) : null}

            <div
              style={{
                minHeight: 300,
                display: "grid",
                alignContent: "center",
                padding:
                  "clamp(16px, 3vw, 30px)",
                borderRadius: 16,
                background: "#fffdf8",
                border:
                  "1px solid rgba(180,83,9,0.14)",
              }}
            >
              {manuscriptText ? (
                activeUnitOversized ? (
                    <div
                      style={{
                        whiteSpace:
                          "pre-wrap",
                        lineHeight: 1.85,
                        fontSize:
                          "clamp(17px, 2vw, 22px)",
                        color: "#1f2937",
                        maxHeight:
                          activeUnitOversized
                            ? "min(68vh, 720px)"
                            : "none",
                        overflowY:
                          activeUnitOversized
                            ? "auto"
                            : "visible",
                        paddingRight:
                          activeUnitOversized
                            ? 12
                            : 0,
                      }}
                    >
                      {manuscriptText}
                    </div>
                ) : (
                  <ChildrensBookManuscriptReadOnly
                    lang={lang}
                    content={
                      activeItem
                        ?.manuscript
                        ?.manuscriptContent
                    }
                    fallbackText={
                      manuscriptText
                    }
                  />
                )
              ) : (
                <p
                  className="ns-card-meta"
                  style={{
                    margin: 0,
                  }}
                >
                  {isFR
                    ? "Aucun texte publié pour cette page."
                    : "No published text is available for this page."}
                </p>
              )}

              {!activeImage &&
              imageError ? (
                <p
                  className="ns-card-meta"
                  style={{
                    margin:
                      "16px 0 0",
                    color: "#991b1b",
                  }}
                >
                  {imageError}
                </p>
              ) : null}
            </div>
          </div>
        ) : (
          <div
            style={{
              minHeight: 360,
              display: "grid",
              placeItems: "center",
              textAlign: "center",
            }}
          >
            <strong>
              {isFR
                ? "Aucun contenu publié"
                : "No published content"}
            </strong>
          </div>
        )}
      </article>

      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          marginTop: 18,
        }}
      >
        <button
          className="ns-button ns-button-outline"
          type="button"
          onClick={onReturn}
        >
          {isFR
            ? "Retour au Blueprint"
            : "Return to Blueprint"}
        </button>

        <button
          className="ns-button ns-button-outline"
          type="button"
          onClick={
            onOpenPublicationHandoff
          }
        >
          {isFR
            ? "Ouvrir les contrôles de publication"
            : "Open Publication Controls"}
        </button>
      </div>
    </section>
  );
}
